import { useCallback, useEffect, useState } from 'react'
import {
  Button, Col, Drawer, Dropdown, Empty, Form, Input, InputNumber, message, Modal, Popconfirm, Row, Select, Space, Table, Tag, Tree, TreeSelect, Typography,
} from 'antd'
import type { MenuProps } from 'antd'
import {
  FileTextOutlined, PlusOutlined, ReloadOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { DataNode } from 'antd/es/tree'
import * as uiApi from '@/api/uiAutomation'
import { useProjectStore } from '@/stores/project'
import type { UIElement, UIPage } from '@/types'
import '../../styles/aurora-page.css'
import '../../styles/aurora-modal.css'

const { Title, Text } = Typography

/** 8 种定位类型：Tag 显示（类型列） */
const selectorTypeMap: Record<string, { text: string; color: string }> = {
  xpath: { text: 'XPath', color: 'purple' },
  css: { text: 'CSS', color: 'blue' },
  id: { text: 'ID', color: 'cyan' },
  name: { text: 'Name', color: 'geekblue' },
  text: { text: '文本', color: 'green' },
  placeholder: { text: '占位符', color: 'orange' },
  label: { text: '标签', color: 'gold' },
  role: { text: '角色', color: 'magenta' },
}

/** 8 种定位类型：新增/编辑下拉选项 */
const SELECTOR_TYPE_OPTIONS = [
  { value: 'xpath', label: 'XPath' },
  { value: 'css', label: 'CSS 选择器' },
  { value: 'id', label: '元素 ID' },
  { value: 'name', label: 'Name 属性' },
  { value: 'text', label: '可见文本' },
  { value: 'placeholder', label: '占位符' },
  { value: 'label', label: '表单标签' },
  { value: 'role', label: 'ARIA 角色' },
]

/** 各定位类型的表达式示例（输入框 placeholder） */
const SELECTOR_PLACEHOLDER: Record<string, string> = {
  xpath: "//input[@id='kw']",
  css: "#kw 或 .search-input",
  id: 'kw',
  name: 'username',
  text: '登录',
  placeholder: '请输入用户名',
  label: '用户名',
  role: 'button=登录',
}

/** 各定位类型的表达式说明（表单 extra） */
const SELECTOR_EXTRA: Record<string, string> = {
  xpath: "XPath 表达式，如 //input[@id='kw']",
  css: 'CSS 选择器，如 #kw、.search-input',
  id: '元素 ID 属性值，如 kw（自动转为 #kw）',
  name: '元素 name 属性值，如 username',
  text: '元素的完整可见文本（精确匹配），如 登录',
  placeholder: '输入框 placeholder 属性文本，如 请输入用户名',
  label: '表单控件的 label 文本，如 用户名',
  role: 'ARIA 角色=可访问名称，如 button=登录；仅角色时填 button',
}

export default function UIElements() {
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0

  // 列表状态
  const [elements, setElements] = useState<UIElement[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [selectorFilter, setSelectorFilter] = useState<string>('')

  // 编辑抽屉
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<UIElement | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm()
  // 当前所选定位类型（驱动表达式输入框的动态提示）
  const selectorType = (Form.useWatch('selector_type', form) as string) || 'xpath'

  // ============ 元素库页面（分组） ============
  const [pages, setPages] = useState<UIPage[]>([])
  // 左侧选中的页面（0=全部）
  const [selectedPageId, setSelectedPageId] = useState(0)
  const [pageModalOpen, setPageModalOpen] = useState(false)
  const [pageEditing, setPageEditing] = useState<UIPage | null>(null)
  const [pageSubmitting, setPageSubmitting] = useState(false)
  const [pageForm] = Form.useForm()
  // 页面树右键菜单
  const [contextMenuPage, setContextMenuPage] = useState<UIPage | null>(null)

  const fetchPages = useCallback(async () => {
    if (!projectId) return
    try {
      setPages(await uiApi.listPages(projectId))
    } catch (e) {
      console.error('[UIElements] 页面列表加载失败:', e)
    }
  }, [projectId])

  /** 收集页面及其全部后代 ID（编辑时用于排除非法上级选项） */
  const collectDescendantIds = (list: UIPage[], rootId: number): number[] => {
    const out: number[] = []
    const walk = (nodes: UIPage[], under: boolean) => {
      nodes.forEach((n) => {
        const isUnder = under || n.id === rootId
        if (isUnder) out.push(n.id)
        if (n.children?.length) walk(n.children, isUnder)
      })
    }
    walk(list, false)
    return out
  }

  /** 打开页面弹窗：record=null 新建（parent 为上级页面，0=顶级），否则编辑 */
  const openPageModal = (record: UIPage | null, parentId = 0) => {
    setPageEditing(record)
    pageForm.resetFields()
    if (record) {
      // parent_id=0 表示顶级页面，置 undefined 让 TreeSelect 默认空着而不是显示 0
      pageForm.setFieldsValue({ parent_id: record.parent_id || undefined, name: record.name, url: record.url })
    } else {
      pageForm.setFieldsValue({ parent_id: parentId || undefined })
    }
    setPageModalOpen(true)
  }

  const handlePageSubmit = async () => {
    try {
      const values = await pageForm.validateFields()
      values.parent_id = Number(values.parent_id) || 0
      setPageSubmitting(true)
      if (pageEditing) {
        await uiApi.updatePage(pageEditing.id, { ...values, project_id: projectId })
        message.success('页面已更新')
      } else {
        await uiApi.createPage({ ...values, project_id: projectId })
        message.success('页面已创建')
      }
      setPageModalOpen(false)
      fetchPages()
    } catch (err) {
      console.error('[UIElements] 页面保存失败:', err)
    } finally {
      setPageSubmitting(false)
    }
  }

  const handlePageDelete = (record: UIPage) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除页面「${record.name}」吗？${record.element_count > 0 ? `其下 ${record.element_count} 个元素将归入未分组，` : ''}子页面将提升为顶级页面。`,
      okText: '确定删除',
      okType: 'danger',
      cancelText: '取消',
      wrapClassName: 'aurora-modal-wrap',
      className: 'aurora-modal',
      onOk: async () => {
        try {
          const res = await uiApi.deletePage(record.id, projectId)
          const affected = res?.affected_elements ?? 0
          message.success(affected > 0 ? `页面已删除，${affected} 个元素已归入未分组` : '页面已删除')
          if (selectedPageId === record.id) setSelectedPageId(0)
          fetchPages()
          fetchElements()
        } catch (err) {
          console.error('[UIElements] 页面删除失败:', err)
        }
      },
    })
  }

  /** 页面树节点右键：定位对应页面并弹出菜单 */
  const handlePageRightClick = (info: { node: { key: React.Key } }) => {
    const page = findPage(pages, Number(info.node.key))
    if (page) setContextMenuPage(page)
  }

  /** 页面右键菜单项（对齐用例/场景模块：编辑 / 删除，不带图标） */
  const buildPageContextMenuItems = (page: UIPage): MenuProps['items'] => [
    {
      key: 'edit',
      label: '编辑页面',
      onClick: () => openPageModal(page),
    },
    {
      key: 'delete',
      label: '删除',
      danger: true,
      onClick: () => handlePageDelete(page),
    },
  ]

  const fetchElements = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await uiApi.listElements({
        project_id: projectId, page, page_size: pageSize, keyword,
        page_id: selectedPageId > 0 ? selectedPageId : undefined,
        selector_type: selectorFilter || undefined,
      })
      setElements(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [projectId, page, pageSize, keyword, selectedPageId, selectorFilter])

  useEffect(() => {
    fetchElements()
  }, [fetchElements])

  useEffect(() => {
    fetchPages()
  }, [fetchPages])

  const handleCreate = () => {
    setEditing(null)
    form.resetFields()
    // 默认归入左侧当前选中的页面（0=未分组，置 undefined 让 TreeSelect 空着）
    form.setFieldsValue({ selector_type: 'xpath', wait_timeout: 5, page_id: selectedPageId || undefined })
    setDrawerOpen(true)
  }

  const handleEdit = (record: UIElement) => {
    setEditing(record)
    form.resetFields()
    form.setFieldsValue({
      page_id: record.page_id || undefined,
      name: record.name,
      selector_type: record.selector_type,
      selector: record.selector,
      description: record.description,
      wait_timeout: record.wait_timeout ?? 5,
    })
    setDrawerOpen(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      // 数字字段转 Number 后再保存（InputNumber 可能返回 null）
      values.wait_timeout = Number(values.wait_timeout) || 5
      values.page_id = Number(values.page_id) || 0
      // 页面地址快照：选择页面时自动带出该页面的 URL（冗余存储，便于详情展示）
      const selectedPage = pages.find((p) => p.id === values.page_id)
      values.page_url = selectedPage?.url || ''
      setSubmitting(true)
      if (editing) {
        await uiApi.updateElement(editing.id, { ...values, project_id: projectId })
        message.success('更新成功')
      } else {
        await uiApi.createElement({ ...values, project_id: projectId })
        message.success('创建成功')
      }
      setDrawerOpen(false)
      fetchElements()
      fetchPages()
    } catch (err) {
      console.error('[UIElements] 保存失败:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await uiApi.deleteElement(id, projectId)
      message.success('删除成功')
      fetchElements()
    } catch (err) {
      console.error('[UIElements] 删除失败:', err)
    }
  }

  /** 递归查找页面名（页面树为嵌套结构，子页面在 children 中） */
  const findPageName = (list: UIPage[], id: number): string => {
    for (const p of list) {
      if (p.id === id) return p.name
      if (p.children?.length) {
        const found = findPageName(p.children, id)
        if (found) return found
      }
    }
    return ''
  }

  const columns: ColumnsType<UIElement> = [
    {
      title: '元素名称',
      dataIndex: 'name',
      width: 200,
      render: (v: string, r: UIElement) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--au-slate)' }}>{v}</div>
          {r.description ? (
            <div style={{ fontSize: 12, color: 'var(--au-slate-muted)', marginTop: 2 }}>{r.description}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'selector_type',
      width: 100,
      render: (v: string) => {
        const s = selectorTypeMap[v] || { text: v, color: 'default' }
        return <Tag color={s.color}>{s.text}</Tag>
      },
    },
    {
      title: '定位表达式',
      dataIndex: 'selector',
      width: 300,
      ellipsis: true,
      render: (v: string) => (
        <Text
          code
          style={{ fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {v}
        </Text>
      ),
    },
    {
      title: '所属页面',
      key: 'page_id',
      width: 180,
      ellipsis: true,
      render: (_, r: UIElement) => {
        const pageName = r.page_id > 0 ? findPageName(pages, r.page_id) : ''
        return (
          <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>
            {pageName || r.page_url || '未分组'}
          </span>
        )
      },
    },
    {
      title: '等待超时(s)',
      dataIndex: 'wait_timeout',
      width: 110,
      align: 'center',
      render: (v: number) => (
        <span style={{ fontSize: 13, color: 'var(--au-slate)' }}>{v ?? '-'}</span>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'update_time',
      width: 170,
      render: (v: string) => (
        <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>
          {v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 140,
      render: (_, r: UIElement) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm
            title="确定删除此元素？"
            onConfirm={() => handleDelete(r.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  /** 页面树数据（Tree 展示用：图标 + 名称，操作走右键菜单，对齐用例模块） */
  const pageTreeData: DataNode[] = (function convert(list: UIPage[]): DataNode[] {
    return list.map((p) => ({
      key: p.id,
      title: (
        <Space>
          <FileTextOutlined style={{ color: 'var(--au-primary)' }} />
          <span>{p.name}</span>
        </Space>
      ),
      children: p.children?.length ? convert(p.children) : undefined,
    }))
  })(pages)

  /** 页面树数据（TreeSelect 下拉用，纯文本 title，与新建页面弹框一致：不带计数） */
  const pageSelectTreeData: { value: number; title: string; children?: { value: number; title: string }[] }[] =
    (function convert(list: UIPage[]): { value: number; title: string; children?: { value: number; title: string }[] }[] {
      return list.map((p) => ({
        value: p.id,
        title: p.name,
        children: p.children?.length ? convert(p.children) : undefined,
      }))
    })(pages)

  /** 递归查找页面节点 */
  const findPage = (list: UIPage[], id: number): UIPage | undefined => {
    for (const p of list) {
      if (p.id === id) return p
      const hit = p.children?.length ? findPage(p.children, id) : undefined
      if (hit) return hit
    }
    return undefined
  }
  const selectedPage = selectedPageId > 0 ? findPage(pages, selectedPageId) : undefined

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>元素库</Title>
          <Tag color="orange">集中管理定位器</Tag>
          <Tag color="blue">跨用例复用</Tag>
        </div>
        <Space>
          <Button onClick={fetchElements}>刷新</Button>
          <Button type="primary" onClick={handleCreate}>新建元素</Button>
        </Space>
      </div>

      <Row gutter={16} style={{ flex: 1, overflow: 'hidden' }}>
        {/* 左侧页面列表（宽度对齐场景编排的场景模块栏） */}
        <Col xs={24} md={5} lg={4} style={{ height: '100%' }}>
          <div className="content-card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="content-card-header" style={{ flexShrink: 0 }}>
              <div className="content-card-title">
                <FileTextOutlined style={{ marginRight: 8, color: 'var(--au-primary)' }} />
                页面列表
              </div>
              <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => openPageModal(null)} title="新建页面" />
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px' }}>
              {/* 页面树（支持子页面层级，右键菜单操作，对齐用例模块）；进入页面默认展示全部，再次点击已选节点回到全部 */}
              {pageTreeData.length > 0 ? (
                <Dropdown
                  menu={{ items: contextMenuPage ? buildPageContextMenuItems(contextMenuPage) : [] }}
                  trigger={['contextMenu']}
                  open={contextMenuPage !== null}
                  onOpenChange={(open) => { if (!open) setContextMenuPage(null) }}
                >
                  <div>
                    <Tree
                      treeData={pageTreeData}
                      defaultExpandAll
                      selectedKeys={selectedPageId > 0 ? [selectedPageId] : []}
                      onSelect={(keys) => {
                        // 点击已选中节点时 keys 为空 → 取消过滤，展示全部元素
                        setSelectedPageId(keys[0] !== undefined ? Number(keys[0]) : 0)
                        setPage(1)
                      }}
                      onRightClick={handlePageRightClick}
                      showLine={{ showLeafIcon: false }}
                    />
                  </div>
                </Dropdown>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无页面" style={{ marginTop: 24 }} />
              )}
            </div>
          </div>
        </Col>

        {/* 右侧元素列表 */}
        <Col xs={24} md={19} lg={20} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="search-bar" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Select
          placeholder="定位类型"
          allowClear
          style={{ width: 150 }}
          value={selectorFilter || undefined}
          onChange={(v) => {
            setSelectorFilter(v || '')
            setPage(1)
          }}
          options={SELECTOR_TYPE_OPTIONS}
        />
        <Input
          allowClear
          placeholder="搜索元素名称"
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value)
            setPage(1)
          }}
          onPressEnter={() => setPage(1)}
          style={{ width: 260 }}
        />
        <Button
          type="primary"
          onClick={() => setPage(1)}
          style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}
        >
          查询
        </Button>
        <Tag color={selectedPageId > 0 ? 'blue' : 'default'}>
          {selectedPageId > 0 ? `页面：${selectedPage?.name || ''}` : '全部页面'}
        </Tag>
      </div>

      <div className="content-card" style={{ flex: 1, overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={elements}
          rowKey="id"
          loading={loading}
          scroll={{ y: '100%' }} /* 表体由 flex 分配高度，分页栏贴 content-card 底部（对齐场景编排列表） */
          pagination={{
            current: page,
            total,
            pageSize,
            showSizeChanger: true,
            showTotal: (t: number) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
        />
      </div>
        </Col>
      </Row>

      {/* ============ 编辑抽屉 ============ */}
      <Drawer
        title={editing ? '编辑元素' : '新建元素'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        rootClassName="aurora-edit-drawer"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={submitting} onClick={handleSubmit}>保存</Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="元素名称" rules={[{ required: true, message: '请输入元素名称' }]}>
            <Input placeholder="如：搜索输入框" maxLength={255} />
          </Form.Item>
          <Form.Item name="selector_type" label="定位类型" rules={[{ required: true, message: '请选择定位类型' }]}>
            <Select options={SELECTOR_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="selector"
            label="定位表达式"
            rules={[{ required: true, message: '请输入定位表达式' }]}
            extra={SELECTOR_EXTRA[selectorType] || SELECTOR_EXTRA.xpath}
          >
            <Input.TextArea
              rows={3}
              placeholder={SELECTOR_PLACEHOLDER[selectorType] || SELECTOR_PLACEHOLDER.xpath}
              maxLength={1024}
            />
          </Form.Item>
          <Form.Item name="page_id" label="所属页面" extra="选填；不选择则为未分组（页面地址将自动关联所选页面）">
            <TreeSelect
              allowClear
              placeholder="不选择则为未分组"
              treeDefaultExpandAll
              treeData={pageSelectTreeData}
            />
          </Form.Item>
          <Form.Item
            name="wait_timeout"
            label="等待超时(s)"
            rules={[{ required: true, message: '请输入等待超时' }]}
            extra="执行用例时，对该元素执行动作的最长等待时间（1~120 秒，默认 5 秒）"
          >
            <InputNumber min={1} max={120} precision={0} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} placeholder="元素用途说明（选填）" maxLength={2000} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* ============ 页面编辑弹窗（添加/修改页面） ============ */}
      <Modal
        title={pageEditing ? '编辑页面' : '新建页面'}
        open={pageModalOpen}
        onCancel={() => setPageModalOpen(false)}
        onOk={handlePageSubmit}
        okText="保存"
        cancelText="取消"
        confirmLoading={pageSubmitting}
        okButtonProps={{ style: { borderRadius: 999 } }}
        cancelButtonProps={{ style: { borderRadius: 999 } }}
        rootClassName="aurora-modal-wrap"
        className="aurora-modal"
        destroyOnClose
      >
        <Form form={pageForm} layout="vertical">
          <Form.Item name="parent_id" label="上级页面" extra="选填；选择后该页面成为对应页面的子页面">
            <TreeSelect
              allowClear
              placeholder="不选择则为顶级页面"
              treeDefaultExpandAll
              treeData={(function convert(list: UIPage[]): { value: number; title: string; children?: { value: number; title: string }[] }[] {
                return list.map((p) => ({
                  value: p.id,
                  title: p.name,
                  children: p.children?.length ? convert(p.children) : undefined,
                }))
              })(
                // 编辑时排除自身及其后代，避免循环引用
                (() => {
                  const exclude = pageEditing ? collectDescendantIds(pages, pageEditing.id) : []
                  const filterOut = (nodes: UIPage[]): UIPage[] =>
                    nodes.filter((n) => !exclude.includes(n.id)).map((n) => ({ ...n, children: n.children ? filterOut(n.children) : undefined }))
                  return filterOut(pages)
                })()
              )}
            />
          </Form.Item>
          <Form.Item name="name" label="页面名称" rules={[{ required: true, message: '请输入页面名称' }]}>
            <Input placeholder="如：登录页 / 商品列表页" maxLength={255} />
          </Form.Item>
          <Form.Item name="url" label="页面地址" extra="选填；元素选择该页面后将自动关联此地址">
            <Input placeholder="https://shop.example.com/login" maxLength={512} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
